import { FC } from "react";
import { useTranslation } from "react-i18next";

import { IUserActionHistoryEntry } from "@ryde/shared";

import { UserActionsHistoryPlaqueButtonStyled } from "./user-actions-history-drawer.styled";
import {
  formatHistoryElapsedAgo,
  formatHistoryPlaqueDisplay,
  isHistoryEntryUnboundDrawerMode,
  plaqueAccentForHistoryEntry,
} from "./user-actions-history.utils";

export type UserActionsHistoryPlaqueItemProps = {
  entry: IUserActionHistoryEntry;
  formatPlaqueWhen: (recordedAt: number) => string;
  plaqueNowMs: number;
  onClickHandler: (entry: IUserActionHistoryEntry) => void;
};

export const UserActionsHistoryPlaqueItem: FC<
  UserActionsHistoryPlaqueItemProps
> = ({ entry, formatPlaqueWhen, plaqueNowMs, onClickHandler }) => {
  const { t } = useTranslation();
  const { label, tailNumber } = formatHistoryPlaqueDisplay(entry, t);
  const lineTitleForAria = tailNumber ? `${label} ${tailNumber}` : label;
  const accent = plaqueAccentForHistoryEntry(entry);
  const isUnboundMode = isHistoryEntryUnboundDrawerMode(entry);
  const when = formatPlaqueWhen(entry.recordedAt);
  const ago = formatHistoryElapsedAgo({
    recordedAtMs: entry.recordedAt,
    nowMs: plaqueNowMs || entry.recordedAt,
  });

  return (
    <UserActionsHistoryPlaqueButtonStyled
      type="button"
      $accent={accent}
      onClick={() => onClickHandler(entry)}
      aria-label={`${t("userActionsHistory.reopenCardVerb", { defaultValue: "Open" })}, ${when}: ${lineTitleForAria}`}
    >
      <span className="UserActionsHistory-PlaqueWhen">{when}</span>
      <span className="UserActionsHistory-PlaqueCenter">
        {isUnboundMode ? (
          <span
            className="UserActionsHistory-PlaqueUnboundGlyph"
            style={{ color: accent }}
            aria-hidden
          >
            {"\u25C6"}
          </span>
        ) : (
          <span className="UserActionsHistory-PlaqueDot" aria-hidden />
        )}
        <span className="UserActionsHistory-PlaqueLabel">{label}</span>
        <span className="UserActionsHistory-PlaqueAgo" aria-hidden>
          {` (${ago})`}
        </span>
      </span>
      <span className="UserActionsHistory-PlaqueId">
        {tailNumber ?? "\u00a0"}
      </span>
    </UserActionsHistoryPlaqueButtonStyled>
  );
};
